/**
 * @fileoverview Displays recent activity using the `RecentActivity` component, fetching `Transaction` data with the `useCreditTransactions` hook. It filters and renders transactions in a table, utilizing components like `TableContainer` and `Table`. The component uses the `useRowExpansion` hook to display additional transaction details.
 */

import React, { useMemo, useState } from 'react'
import { useCreditTransactions } from '../hooks/useUserData'
import {
  TableContainer,
  Table,
  THead,
  TBody,
  Tr,
  Th,
  Td,
  RowExpander,
  useRowExpansion,
} from './Table'
import '../styles/components/RecentActivity.css'
import PageContainer from './PageContainer'

// Interface for transaction data (based on the structure used in Billing)
interface Transaction {
  id?: string
  timestamp: Date | { toDate(): Date }
  amount: number
  details: string
  source: string
  metadata?: {
    args?: any
    apiKeyName?: string
  }
}

interface RecentActivityProps {}

const RecentActivity: React.FC<RecentActivityProps> = () => {
  const { transactions, loading } = useCreditTransactions(100)
  const [typeFilter, setTypeFilter] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const { isExpanded, getToggleProps } = useRowExpansion()

  // Filter transactions based on type and date range
  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction: Transaction) => {
      // Filter by type
      if (typeFilter !== 'all' && transaction.source !== typeFilter) {
        return false
      }

      // Filter by date range
      if (startDate) {
        const transactionDate =
          transaction.timestamp instanceof Date
            ? transaction.timestamp
            : transaction.timestamp.toDate()
        const startDateObj = new Date(startDate)
        if (transactionDate < startDateObj) {
          return false
        }
      }

      if (endDate) {
        const transactionDate =
          transaction.timestamp instanceof Date
            ? transaction.timestamp
            : transaction.timestamp.toDate()
        const endDateObj = new Date(endDate)
        endDateObj.setHours(23, 59, 59, 999) // Set to end of day
        if (transactionDate > endDateObj) {
          return false
        }
      }

      return true
    })
  }, [transactions, typeFilter, startDate, endDate])

  const formatTimestamp = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date)
  }

  return (
    <PageContainer>
      <div className="recent-activity-section">
        <h2>Recent Activity</h2>
        <div className="transaction-filters">
          <div className="filter-group">
            <label htmlFor="type-filter">Type:</label>
            <select
              id="type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="subscription">Subscription</option>
              <option value="usage">Usage</option>
              <option value="initial">Initial</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="start-date">From:</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="date-filter"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="end-date">To:</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="date-filter"
            />
          </div>
        </div>

        <>
          {loading ? (
            <p className="loading-text">Loading transactions...</p>
          ) : filteredTransactions.length === 0 ? (
            <p className="empty-transactions">No transactions matching the selected filters.</p>
          ) : (
            <TableContainer className="transactions-table-container">
              <Table className="transactions-table">
                <THead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Credits</Th>
                    <Th>Details</Th>
                  </Tr>
                </THead>
                <TBody>
                  {filteredTransactions.map((transaction: Transaction) => {
                    const timestamp =
                      transaction.timestamp instanceof Date
                        ? transaction.timestamp
                        : transaction.timestamp.toDate()

                    const isUsage = transaction.source === 'usage'
                    const hasMetadataArgs = isUsage && transaction.metadata?.args
                    const rowId = transaction.id || `transaction-${timestamp.getTime()}`

                    const expandedContent = (
                      <div className="metadata-container">
                        <pre className="metadata-code">
                          {JSON.stringify(
                            {
                              ...transaction.metadata?.args,
                              apiKeyUsed: transaction.metadata?.apiKeyName,
                            },
                            null,
                            2,
                          )}
                        </pre>
                      </div>
                    )

                    return (
                      <RowExpander
                        key={rowId}
                        hasExpandableContent={hasMetadataArgs}
                        {...getToggleProps(rowId)}
                        expandedContent={expandedContent}
                        colSpan={3}
                      >
                        <Td className="date-cell monospace">{formatTimestamp(timestamp)}</Td>
                        <Td className="amount-cell monospace">
                          <span
                            className={
                              transaction.amount >= 0 ? 'amount-positive' : 'amount-negative'
                            }
                          >
                            {transaction.amount > 0 ? '+' : ''}
                            {transaction.amount}
                          </span>
                        </Td>
                        <Td className="details-cell monospace">
                          {transaction.details}
                          {hasMetadataArgs && (
                            <span className="expand-indicator">
                              {isExpanded(rowId) ? '▼' : '▶'}
                            </span>
                          )}
                        </Td>
                      </RowExpander>
                    )
                  })}
                </TBody>
              </Table>
            </TableContainer>
          )}
        </>
      </div>
    </PageContainer>
  )
}

export default RecentActivity
